import * as Slider from '@radix-ui/react-slider';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { cn } from '../lib/cn';
import { Button } from './button';
import { Typography } from './typography';

type Indicator = {
  label: string;
  value: number;
};

type AdvancedSliderProps = {
  label?: string;
  topIndicator?: Indicator;
  bottomIndicator?: Indicator;
  className?: string;
} & Slider.SliderProps;

// for our thumb w-6 / h-6, to calculate the position of indicators
const THUMB_PX = 24;

export const AdvancedSlider = forwardRef<HTMLSpanElement, AdvancedSliderProps>(
  (
    {
      label,
      topIndicator,
      bottomIndicator,
      className,
      step = 1,
      value = [50],
      min = 0,
      max = 100,
      onValueCommit,
      onValueChange,
      ...props
    },
    ref
  ) => {
    const [currentValue, setCurrentValue] = useState(value);
    const holdTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastParentValueRef = useRef(value);

    // Continuously adjusting value during arrow button hold with interval
    const handleHold = (holdStep: number) => {
      if (intervalRef.current) clearInterval(intervalRef.current);

      // Waiting certain period of time before triggering to prevent adjusting value
      // with multiple actions at the same time
      holdTimeoutRef.current = setTimeout(() => {
        // Continuous adjusting starts
        intervalRef.current = setInterval(() => {
          setCurrentValue((prev) => {
            const updatedValue =
              holdStep > 0
                ? [Math.min(max, prev[0] + holdStep)]
                : [Math.max(min, prev[0] + holdStep)];
            lastParentValueRef.current = updatedValue;
            onValueChange?.(updatedValue);
            return updatedValue;
          });
        }, 100);
      }, 300);
    };

    // Stop updating when user releases button or moves out of button boundaries
    const stopHold = () => {
      if (holdTimeoutRef.current) {
        clearInterval(holdTimeoutRef.current);
        holdTimeoutRef.current = null;
      }

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        onValueCommit?.(currentValue);
      }
    };

    const getLeftWithRadixOffset = (v: number) => {
      const range = max - min;
      const rawPercent = range > 0 ? ((v - min) / range) * 100 : 0;
      // An indicator outside [min, max] would otherwise render past the track and
      // overflow its container, so pin it to the nearest end instead.
      const percent = Math.min(100, Math.max(0, rawPercent));
      const offsetPx = ((50 - percent) / 50) * (THUMB_PX / 2);
      return `calc(${percent}% + ${offsetPx}px)`;
    };

    useEffect(() => {
      if (value?.[0] !== lastParentValueRef.current?.[0]) {
        setCurrentValue(value);
      }
    }, [value]);

    return (
      <div className={bottomIndicator && 'mb-8'}>
        <Typography
          className={cn(
            'text-neutral-8 ml-1 hover:cursor-default',
            topIndicator && 'mb-7'
          )}
        >
          {label}
        </Typography>
        <div className="flex items-center gap-x-1">
          <Button
            iconed
            variant="text"
            leftIconName="chevron-left"
            className="text-primary-4 p-0"
            onMouseUp={stopHold}
            onTouchEnd={stopHold}
            onMouseLeave={stopHold} // in case user drags cursor out
            onMouseDown={() => handleHold(-step)}
            onTouchStart={() => handleHold(-step)}
            onClick={() => {
              setCurrentValue((prev) => {
                const updatedValue = [Math.max(min, prev[0] - step)];
                lastParentValueRef.current = updatedValue;
                onValueCommit?.(updatedValue);
                onValueChange?.(updatedValue);
                return updatedValue;
              });
            }}
          />
          <Slider.Root
            max={max}
            min={min}
            ref={ref}
            step={step}
            value={[currentValue[0]]}
            onValueCommit={onValueCommit}
            onValueChange={(val) => {
              lastParentValueRef.current = [val[0]];
              setCurrentValue([val[0]]);
              onValueChange?.([val[0]]);
            }}
            {...props}
            className={cn('relative flex h-5 w-full items-center', className)}
          >
            <Slider.Track className="bg-neutral-5 relative h-3 grow rounded-full">
              <Slider.Range className="bg-primary-4 absolute h-full rounded-full" />
            </Slider.Track>
            <Slider.Thumb className="border-primary-6 bg-primary-4 block h-6 w-6 rounded-full border-4 shadow-md hover:cursor-col-resize focus:outline-0" />
            {topIndicator && (
              <div
                className="absolute -top-5.5 flex -translate-x-1/2 flex-col items-center duration-300 ease-in-out"
                style={{ left: getLeftWithRadixOffset(topIndicator.value) }}
              >
                <Typography
                  noWrap
                  className="text-neutral-8 text-xs hover:cursor-default"
                >
                  {topIndicator?.label}
                </Typography>

                <div className="border-t-primary-8 h-0 w-0 self-center border-t-[8px] border-r-[10px] border-l-[10px] border-r-transparent border-l-transparent" />
              </div>
            )}
            {bottomIndicator && (
              <div
                className="absolute top-4.5 flex -translate-x-1/2 flex-col items-center duration-300 ease-in-out"
                style={{ left: getLeftWithRadixOffset(bottomIndicator.value) }}
              >
                <div className="border-b-primary-8 h-0 w-0 self-center border-r-[10px] border-b-[8px] border-l-[10px] border-r-transparent border-l-transparent" />
                <Typography
                  noWrap
                  className="text-neutral-8 text-xs hover:cursor-default"
                >
                  {bottomIndicator?.label}
                </Typography>
              </div>
            )}
          </Slider.Root>
          <Button
            iconed
            variant="text"
            leftIconName="chevron-right"
            className="text-primary-4 p-0"
            onMouseUp={stopHold}
            onTouchEnd={stopHold}
            onMouseLeave={stopHold}
            onMouseDown={() => handleHold(step)}
            onTouchStart={() => handleHold(step)}
            onClick={() => {
              setCurrentValue((prev) => {
                const updatedValue = [Math.min(max, prev[0] + step)];
                lastParentValueRef.current = updatedValue;
                onValueCommit?.(updatedValue);
                onValueChange?.(updatedValue);
                return updatedValue;
              });
            }}
          />
        </div>
      </div>
    );
  }
);

AdvancedSlider.displayName = 'AdvancedSlider';
